import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState } from "react";
import { Award, Brain, Check, FileDown, HelpCircle, Languages, ListChecks, Loader2, Lock, Quote, RotateCcw, Save, Sparkles, Wand2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  explainPrompt,
  buildOutline,
  checkThesis,
  suggestThesisQuestions,
  suggestParagraphQuestions,
  checkParagraph,
  checkClaim,
  findQuotes,
  gradeBrainwork,
  saveTutorWork,
  loadTutorWork,
  autoCorrectText,
} from "@/lib/tutor.functions";
import { useAccess } from "@/hooks/useAccess";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { PastDueBanner } from "@/components/PastDueBanner";
import { SiteHeader } from "@/components/SiteHeader";
import { MicButton } from "@/components/MicButton";
import { SourceDropzone } from "@/components/SourceDropzone";

export const Route = createFileRoute("/tutor")({
  validateSearch: (search: Record<string, unknown>) => ({
    id: typeof search.id === "string" ? search.id : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Build Your Own (Tutor) — SmartEssay" },
      { name: "description", content: "SmartEssay.AI Tutor helps you think through your essay with guiding questions — without writing it for you." },
      { property: "og:title", content: "Build Your Own — SmartEssay.AI Tutor" },
      { property: "og:description", content: "Get guided through your essay with smart questions. You do the thinking; we coach you." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: TutorPage,
});

type OutlineData = {
  thesis_hint: string;
  introduction: { target_words: number; points: string[] };
  body_paragraphs: { topic: string; target_words: number; points: string[] }[];
  conclusion: { target_words: number; points: string[] };
};

const ESSAY_TYPES = [
  "Argumentative",
  "Persuasive",
  "Expository",
  "Narrative",
  "Descriptive",
  "Analytical / Literary",
  "Compare & Contrast",
  "Cause & Effect",
  "Research",
  "Reflective",
  "Other",
];

function countWords(s: string) {
  const t = s.trim();
  if (!t) return 0;
  return t.split(/\s+/).length;
}

function TutorPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const explainFn = useServerFn(explainPrompt);
  const buildOutlineFn = useServerFn(buildOutline);
  const saveWorkFn = useServerFn(saveTutorWork);
  const loadWorkFn = useServerFn(loadTutorWork);

  const [checking, setChecking] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const access = useAccess(userId);

  const [sourceTitle, setSourceTitle] = useState("");
  const [sourceText, setSourceText] = useState("");

  // Setup state
  const [essayType, setEssayType] = useState<string>("Argumentative");
  const [teacherPrompt, setTeacherPrompt] = useState("");
  const [plainExplanation, setPlainExplanation] = useState<string | null>(null);
  const [explaining, setExplaining] = useState(false);
  const [bodyParagraphs, setBodyParagraphs] = useState<number>(3);
  const [totalWords, setTotalWords] = useState<number | "">(800);
  const [building, setBuilding] = useState(false);
  const [outline, setOutline] = useState<OutlineData | null>(null);
  const [drafts, setDrafts] = useState<string[]>([]); // per section: 0=intro, 1..N=body, last=conclusion
  const [thesis, setThesis] = useState<string>("");

  // Save / resume
  const [workId, setWorkId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const loadedRef = useRef<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) navigate({ to: "/auth" });
      else {
        setUserId(data.session.user.id);
        setChecking(false);
      }
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      if (!session) navigate({ to: "/auth" });
      else setUserId(session.user.id);
    });
    return () => sub.subscription.unsubscribe();
  }, [navigate]);

  async function onExplain() {
    const p = teacherPrompt.trim();
    if (!p) {
      toast.error("Paste the teacher's prompt first.");
      return;
    }
    if (!access.hasAccess) {
      navigate({ to: "/pricing" });
      return;
    }
    setExplaining(true);
    try {
      const res = await explainFn({ data: { prompt: p } });
      setPlainExplanation(res.explanation);
    } catch (err) {
      toast.error((err as Error).message || "Couldn't explain prompt.");
    } finally {
      setExplaining(false);
    }
  }

  async function onBuildOutline() {
    const p = teacherPrompt.trim();
    if (!p) {
      toast.error("Paste the teacher's prompt first.");
      return;
    }
    if (!bodyParagraphs || bodyParagraphs < 1) {
      toast.error("How many body paragraphs do you need?");
      return;
    }
    if (!access.hasAccess) {
      navigate({ to: "/pricing" });
      return;
    }
    setBuilding(true);
    try {
      const res = await buildOutlineFn({
        data: {
          essay_type: essayType,
          prompt: p,
          body_paragraphs: bodyParagraphs,
          total_words: typeof totalWords === "number" ? totalWords : undefined,
          source_title: sourceTitle.trim() || undefined,
          source_text: sourceText.trim() || undefined,
        },
      });
      setOutline(res.outline);
      setDrafts(new Array(res.outline.body_paragraphs.length + 2).fill(""));
      access.refresh();
    } catch (err) {
      toast.error((err as Error).message || "Couldn't build outline.");
    } finally {
      setBuilding(false);
    }
  }

  // Load saved tutor session if ?id=... is in the URL
  useEffect(() => {
    const id = search.id;
    if (!id || !userId || loadedRef.current === id) return;
    loadedRef.current = id;
    setWorkId(id);
    (async () => {
      try {
        const row = await loadWorkFn({ data: { id } });
        const out = (row?.output ?? null) as {
          kind?: string;
          essay_type?: string;
          teacher_prompt?: string;
          body_paragraphs?: number;
          total_words?: number | null;
          source_title?: string;
          outline?: OutlineData | null;
          drafts?: string[];
          thesis?: string;
        } | null;
        if (!out) return;
        if (out.essay_type) setEssayType(out.essay_type);
        if (typeof out.teacher_prompt === "string") setTeacherPrompt(out.teacher_prompt);
        if (out.body_paragraphs) setBodyParagraphs(out.body_paragraphs);
        setTotalWords(typeof out.total_words === "number" ? out.total_words : "");
        if (typeof out.source_title === "string") setSourceTitle(out.source_title);
        if (typeof row?.source_text === "string") setSourceText(row.source_text ?? "");
        if (out.outline) {
          setOutline(out.outline);
          setDrafts(out.drafts ?? new Array(out.outline.body_paragraphs.length + 2).fill(""));
        }
        if (typeof out.thesis === "string") setThesis(out.thesis);
        toast.success("Picked up where you left off.");
      } catch (err) {
        toast.error((err as Error).message || "Couldn't load saved work.");
      }
    })();
  }, [search.id, userId, loadWorkFn]);

  async function onSave(opts?: { silent?: boolean }) {
    const silent = opts?.silent ?? false;
    if (!teacherPrompt.trim() && !outline) {
      if (!silent) toast.error("Add a prompt or build an outline first, then save.");
      return;
    }
    setSaving(true);
    try {
      const res = await saveWorkFn({
        data: {
          id: workId ?? undefined,
          work: {
            essay_type: essayType,
            teacher_prompt: teacherPrompt,
            body_paragraphs: bodyParagraphs,
            total_words: typeof totalWords === "number" ? totalWords : null,
            source_title: sourceTitle,
            source_text: sourceText,
            outline: outline,
            drafts: drafts,
            thesis: thesis,
          },
        },
      });
      if (!workId) {
        setWorkId(res.id);
        navigate({ to: "/tutor", search: { id: res.id }, replace: true });
      }
      setLastSavedAt(new Date());
      if (!silent) toast.success("Your work is saved.");
    } catch (err) {
      if (!silent) toast.error((err as Error).message || "Couldn't save your work.");
    } finally {
      setSaving(false);
    }
  }

  function onClearForm() {
    if (!window.confirm("Start over? This will clear everything in the tutor.")) return;
    setSourceTitle("");
    setSourceText("");
    setEssayType("Argumentative");
    setTeacherPrompt("");
    setPlainExplanation(null);
    setBodyParagraphs(3);
    setTotalWords(800);
    setOutline(null);
    setDrafts([]);
    setThesis("");
    setWorkId(null);
    setLastSavedAt(null);
    loadedRef.current = null;
    navigate({ to: "/tutor", replace: true });
    toast.success("Form cleared — ready for a new essay.");
  }

  // Autosave: debounced silent save whenever the student's work changes.
  const autosaveReady = !checking && !!userId && (!!teacherPrompt.trim() || !!outline);
  const savingRef = useRef(false);
  useEffect(() => {
    savingRef.current = saving;
  }, [saving]);
  useEffect(() => {
    if (!autosaveReady) return;
    const t = setTimeout(() => {
      if (savingRef.current) return;
      onSave({ silent: true });
    }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    autosaveReady,
    essayType,
    teacherPrompt,
    bodyParagraphs,
    totalWords,
    sourceTitle,
    sourceText,
    outline,
    drafts,
    thesis,
  ]);

  // Save before the page unloads (catch-all for forgotten saves).
  useEffect(() => {
    function handler() {
      if (!autosaveReady || savingRef.current) return;
      onSave({ silent: true });
    }
    window.addEventListener("beforeunload", handler);
    window.addEventListener("pagehide", handler);
    return () => {
      window.removeEventListener("beforeunload", handler);
      window.removeEventListener("pagehide", handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autosaveReady]);

  if (checking) return <div className="grid min-h-screen place-items-center text-muted-foreground">Loading…</div>;

  const locked = !access.loading && !access.hasAccess;

  return (
    <div className="min-h-screen bg-hero-gradient">
      <PaymentTestModeBanner />
      <PastDueBanner show={access.isPastDue} />
      <SiteHeader />

      <main className="mx-auto max-w-3xl px-6 pb-24 pt-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 border-l-2 border-gold pl-3 text-xs font-medium uppercase tracking-[0.18em] text-primary">
              <Brain className="h-3.5 w-3.5" /> Build Your Own
            </div>
            <h1 className="font-display text-4xl font-medium leading-tight tracking-tight sm:text-5xl">
              SmartEssay.AI <em>Tutor</em>
            </h1>
            <div className="mt-4 h-px w-16 bg-gold/70" aria-hidden />
            <p className="mt-4 max-w-xl text-muted-foreground">
              A coach that never writes your essay for you. You think; I'll keep asking the right questions until your ideas click. Save your work anytime and come back to finish it later.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild type="button" variant="ghost" size="sm" className="gap-1.5">
              <Link to="/history"><Save className="h-3.5 w-3.5" /> My saved work</Link>
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={onClearForm} className="gap-1.5">
              <RotateCcw className="h-3.5 w-3.5" /> Start over
            </Button>
          </div>
        </div>

        {/* Step progress — always visible so users know where they are */}
        {(() => {
          const conclusionDraft = drafts.length > 0 ? drafts[drafts.length - 1] : "";
          const anyDraft = drafts.some((d) => (d || "").trim().length > 0);
          const currentStep = !outline ? 1 : !anyDraft ? 2 : conclusionDraft.trim() ? 4 : 3;
          const steps = [
            { n: 1, label: "Setup", href: "#step-setup" },
            { n: 2, label: "Outline", href: "#step-outline" },
            { n: 3, label: "Draft", href: "#step-outline" },
            { n: 4, label: "Finish", href: "#step-finish" },
          ];
          return (
            <nav aria-label="Tutor progress" className="sticky top-20 z-30 mt-6 -mx-2 overflow-x-auto rounded-full border border-border bg-background/80 px-2 py-2 backdrop-blur">
              <ol className="flex min-w-max items-center gap-1 px-1 text-xs sm:gap-2 sm:text-sm">
                {steps.map((s, i) => {
                  const isDone = s.n < currentStep;
                  const isCurrent = s.n === currentStep;
                  return (
                    <li key={s.n} className="flex items-center gap-1 sm:gap-2">
                      <a
                        href={s.href}
                        className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium transition-colors sm:px-3 ${
                          isCurrent
                            ? "bg-primary text-primary-foreground"
                            : isDone
                            ? "bg-primary/10 text-primary"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        <span className={`grid h-4 w-4 place-items-center rounded-full text-[10px] font-bold sm:h-5 sm:w-5 sm:text-[11px] ${
                          isCurrent ? "bg-primary-foreground text-primary" : isDone ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                        }`}>
                          {isDone ? <Check className="h-3 w-3" /> : s.n}
                        </span>
                        {s.label}
                      </a>
                      {i < steps.length - 1 && <span className="text-muted-foreground/50">›</span>}
                    </li>
                  );
                })}
              </ol>
            </nav>
          );
        })()}

        {!access.loading && !access.isSubscribed && (
          <div className={`mt-6 flex items-center justify-between gap-4 rounded-xl border p-4 ${access.hasAccess ? "border-border bg-card" : "border-primary bg-primary/5"}`}>
            <div className="flex items-start gap-3">
              <Lock className="mt-0.5 h-5 w-5 text-primary" />
              <div className="text-sm">
                <div className="font-medium">
                  {access.hasAccess
                    ? `Free session available (${access.freeQuota - access.generationCount} of ${access.freeQuota} left)`
                    : "You've used your free session"}
                </div>
                <div className="text-muted-foreground">
                  {access.hasAccess ? "Tutor sessions share the same free quota as outlines and reviews." : "Subscribe to keep tutoring sessions."}
                </div>
              </div>
            </div>
            <Button asChild size="sm" variant={access.hasAccess ? "outline" : "default"}>
              <Link to="/pricing">{access.hasAccess ? "See plans" : "Unlock"}</Link>
            </Button>
          </div>
        )}

        {/* Essay setup */}
        <section id="step-setup" className="mt-8 space-y-5 scroll-mt-32 rounded-2xl border border-border bg-card p-5 shadow-soft sm:p-6">
          <div className="flex items-center gap-3 border-l-2 border-gold pl-3">
            <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary text-xs font-bold text-primary-foreground">1</span>
            <ListChecks className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-medium uppercase tracking-[0.18em] text-primary">Essay setup</h2>
            <span className="ml-auto text-[11px] text-muted-foreground">Tell me about the assignment</span>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                What type of essay are you writing?
              </Label>
              <Select value={essayType} onValueChange={setEssayType} disabled={locked}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose an essay type" />
                </SelectTrigger>
                <SelectContent>
                  {ESSAY_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                Not sure? Pick the one closest to your teacher&apos;s instructions — you can always change it later.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                How many body paragraphs?
              </Label>
              <Select
                value={String(bodyParagraphs)}
                onValueChange={(v) => setBodyParagraphs(parseInt(v, 10))}
                disabled={locked}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n} {n === 1 ? "paragraph" : "paragraphs"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-end justify-between gap-2">
              <Label htmlFor="teacher-prompt" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Teacher's prompt / question
              </Label>
              <span className="text-[11px] text-muted-foreground">{countWords(teacherPrompt)} words</span>
            </div>
            <div className="relative">
              <Textarea
                id="teacher-prompt"
                value={teacherPrompt}
                onChange={(e) => { setTeacherPrompt(e.target.value); setPlainExplanation(null); }}
                placeholder='Paste exactly what your teacher asked, e.g. "Analyze how Gatsby uses symbols to represent the American Dream."'
                className="min-h-[110px] pr-12"
                disabled={locked}
              />
              <div className="absolute right-2 top-2">
                <MicButton
                  label="teacher's prompt"
                  value={teacherPrompt}
                  onChange={(value) => { setTeacherPrompt(value); setPlainExplanation(null); }}
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={onExplain}
                disabled={locked || explaining || !teacherPrompt.trim()}
              >
                {explaining ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Languages className="mr-1.5 h-3.5 w-3.5" />}
                Break it down in plain language
              </Button>
            </div>
            {plainExplanation && (
              <div className="mt-2 rounded-lg border border-gold/40 bg-accent/40 p-3 text-sm">
                <div className="mb-1 inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-primary">
                  <Sparkles className="h-3.5 w-3.5 text-gold" /> In plain language
                </div>
                <p className="whitespace-pre-wrap">{plainExplanation}</p>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Total word target
            </Label>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={totalWords}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "");
                if (v === "") setTotalWords("");
                else setTotalWords(parseInt(v, 10));
              }}
              onBlur={() => {
                if (totalWords === "") return;
                setTotalWords(Math.max(0, Math.min(20000, totalWords)));
              }}
              placeholder="800"
              disabled={locked}
              className="sm:max-w-[200px]"
            />
            <p className="text-[11px] text-muted-foreground">Used to split target words per section.</p>
          </div>

          {/* Source (optional) */}
          <div className="grid gap-4 border-t border-border pt-5 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="src-title" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Source title (optional)
              </Label>
              <div className="relative">
                <Input
                  id="src-title"
                  value={sourceTitle}
                  onChange={(e) => setSourceTitle(e.target.value)}
                  placeholder="e.g. The Great Gatsby"
                  disabled={locked}
                  className="pr-12"
                />
                <div className="absolute right-1.5 top-1/2 -translate-y-1/2">
                  <MicButton
                    label="source title"
                    value={sourceTitle}
                    onChange={setSourceTitle}
                  />
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="src-text" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Sources (optional — add as many as you need)
              </Label>
              <SourceDropzone
                value={sourceText}
                onChange={setSourceText}
                disabled={locked}
                appendOnUpload
              />
              <div className="relative">
                <Textarea
                  id="src-text"
                  value={sourceText}
                  onChange={(e) => setSourceText(e.target.value)}
                  placeholder="Paste an excerpt or passage you're writing about. Drop more files above to add additional sources."
                  className="min-h-[80px] pr-12"
                  disabled={locked}
                />
                <div className="absolute right-2 top-2">
                  <MicButton
                    label="source text"
                    value={sourceText}
                    onChange={setSourceText}
                  />
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Drop a second file to add it as another source — your existing source won't be overwritten.
              </p>
            </div>
          </div>


          <div>
            <Button onClick={onBuildOutline} disabled={locked || building || !teacherPrompt.trim()}>
              {building ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Wand2 className="mr-1.5 h-4 w-4" />}
              {outline ? "Rebuild outline" : "Build my outline"}
            </Button>
          </div>
        </section>

        {/* Outline */}
        {outline && (
          <div id="step-outline" className="scroll-mt-32">
            <OutlineView
              outline={outline}
              drafts={drafts}
              setDraft={(i, v) => setDrafts((d) => d.map((x, idx) => (idx === i ? v : x)))}
              disabled={locked}
              essayType={essayType}
              teacherPrompt={teacherPrompt}
              sourceTitle={sourceTitle}
              sourceText={sourceText}
              thesis={thesis}
              setThesis={setThesis}
              onSave={onSave}
              onClearForm={onClearForm}
              saving={saving}
              workId={workId}
              lastSavedAt={lastSavedAt}
            />
          </div>
        )}
      </main>
    </div>
  );
}

function SectionDraft({
  label,
  target,
  value,
  onChange,
  points,
  disabled,
  sectionKind,
  sectionTopic,
  thesis,
  essayType,
  teacherPrompt,
  sourceTitle,
  sourceText,
}: {
  label: string;
  target: number;
  value: string;
  onChange: (v: string) => void;
  points: string[];
  disabled?: boolean;
  sectionKind: "introduction" | "body" | "conclusion";
  sectionTopic?: string;
  thesis: string;
  essayType: string;
  teacherPrompt: string;
  sourceTitle: string;
  sourceText: string;
}) {
  const words = countWords(value);
  const onTrack = target > 0 ? Math.abs(words - target) <= Math.max(20, target * 0.15) : true;

  const suggestFn = useServerFn(suggestParagraphQuestions);
  const checkFn = useServerFn(checkParagraph);
  const quotesFn = useServerFn(findQuotes);
  const autoCorrectFn = useServerFn(autoCorrectText);
  const [autoCorrecting, setAutoCorrecting] = useState(false);
  const [justCorrected, setJustCorrected] = useState(false);
  const lastCorrectedRef = useRef<string>("");
  const valueRef = useRef(value);
  useEffect(() => { valueRef.current = value; }, [value]);

  useEffect(() => {
    if (disabled) return;
    const trimmed = value.trim();
    if (trimmed.length < 12) return;
    // Only auto-correct at a natural pause: ends with sentence punctuation or newline
    if (!/[.!?\n]\s*$/.test(value)) return;
    if (lastCorrectedRef.current === value) return;
    const timer = setTimeout(async () => {
      const snapshot = value;
      try {
        setAutoCorrecting(true);
        const res = await autoCorrectFn({ data: { text: snapshot } });
        const corrected = res.corrected;
        // Only apply if user hasn't typed more in the meantime, and there's an actual change
        if (valueRef.current === snapshot && corrected && corrected !== snapshot) {
          lastCorrectedRef.current = corrected;
          onChange(corrected);
          setJustCorrected(true);
          setTimeout(() => setJustCorrected(false), 1800);
        } else {
          lastCorrectedRef.current = snapshot;
        }
      } catch {
        // silent — autocorrect should never interrupt the student
      } finally {
        setAutoCorrecting(false);
      }
    }, 1500);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, disabled]);
  const [suggesting, setSuggesting] = useState(false);
  const [starterQuestions, setStarterQuestions] = useState<string[] | null>(null);
  const [checking, setChecking] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [quoteCount, setQuoteCount] = useState<number>(2);
  const [findingQuotes, setFindingQuotes] = useState(false);
  const [quotes, setQuotes] = useState<{ quote: string; citation: string; why_it_works: string }[] | null>(null);
  const [feedback, setFeedback] = useState<{
    rating: "great" | "almost" | "needs_work";
    praise: string;
    suggestions: string[];
    encouragement: string;
  } | null>(null);

  const ratingStyles: Record<string, { label: string; chip: string }> = {
    great: { label: "Great paragraph!", chip: "bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/30" },
    almost: { label: "Almost there", chip: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30" },
    needs_work: { label: "Good start — let's strengthen it", chip: "bg-primary/15 text-primary border-primary/30" },
  };

  async function onSuggest() {
    setSuggesting(true);
    try {
      const res = await suggestFn({
        data: {
          section_kind: sectionKind,
          section_label: label,
          section_topic: sectionTopic || undefined,
          section_points: points,
          thesis: thesis || undefined,
          essay_type: essayType || undefined,
          prompt: teacherPrompt || undefined,
          source_title: sourceTitle || undefined,
          source_text: sourceText || undefined,
        },
      });
      setStarterQuestions(res.questions);
    } catch (err) {
      toast.error((err as Error).message || "Couldn't get suggestions. Try again.");
    } finally {
      setSuggesting(false);
    }
  }

  async function onCheck() {
    const t = value.trim();
    if (!t) {
      toast.error("Write something first — even a rough draft is great!");
      return;
    }
    setChecking(true);
    setFeedback(null);
    try {
      const nextAttempt = attempts + 1;
      const res = await checkFn({
        data: {
          section_kind: sectionKind,
          section_label: label,
          section_topic: sectionTopic || undefined,
          draft: t,
          thesis: thesis || undefined,
          essay_type: essayType || undefined,
          prompt: teacherPrompt || undefined,
          source_title: sourceTitle || undefined,
          source_text: sourceText || undefined,
          attempt: nextAttempt,
        },
      });
      setAttempts(nextAttempt);
      setFeedback(res.feedback);
    } catch (err) {
      toast.error((err as Error).message || "Couldn't check your paragraph. Try again.");
    } finally {
      setChecking(false);
    }
  }

  async function onFindQuotes() {
    if ((!sourceText || sourceText.trim().length < 20) && (!sourceTitle || sourceTitle.trim().length < 2)) {
      toast.error("Add a source title or paste your source text up top first.");
      return;
    }
    setFindingQuotes(true);
    try {
      const res = await quotesFn({
        data: {
          section_kind: sectionKind,
          section_label: label,
          section_topic: sectionTopic || undefined,
          count: quoteCount,
          thesis: thesis || undefined,
          draft: value.trim() || undefined,
          essay_type: essayType || undefined,
          prompt: teacherPrompt || undefined,
          source_title: sourceTitle || undefined,
          source_text: sourceText,
        },
      });
      setQuotes(res.quotes);
      if (res.quotes.length === 0) {
        toast.message("Couldn't find a clean match. Try a different number or refine your topic.");
      }
    } catch (err) {
      toast.error((err as Error).message || "Couldn't find quotes. Try again.");
    } finally {
      setFindingQuotes(false);
    }
  }

  async function copyQuote(q: { quote: string; citation: string }) {
    try {
      await navigator.clipboard.writeText(`"${q.quote}" ${q.citation}`.trim());
      toast.success("Quote copied — paste it into your paragraph.");
    } catch {
      toast.error("Couldn't copy. Select the text manually.");
    }
  }

  return (
    <div className="rounded-xl border border-border bg-background/40 p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="font-display text-base font-medium">{label}</h3>
        <span className={`text-xs ${onTrack ? "text-muted-foreground" : "text-primary"}`}>
          {words}{target ? ` / ~${target}` : ""} words
        </span>
      </div>
      {points.length > 0 && (
        <div className="mb-3 space-y-1.5">
          <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Think about these questions
          </p>
          <ul className="space-y-1 text-sm text-foreground/80">
            {points.map((p, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-primary" aria-hidden>—</span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mb-3">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onSuggest}
          disabled={disabled || suggesting}
          className="gap-1.5"
        >
          {suggesting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <HelpCircle className="h-3.5 w-3.5" />
          )}
          {starterQuestions ? "Get more questions" : `Help me start my ${sectionKind === "body" ? "paragraph" : sectionKind}`}
        </Button>
        {starterQuestions && starterQuestions.length > 0 && (
          <div className="mt-3 rounded-lg border border-gold/40 bg-accent/30 p-3">
            <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-primary">
              Questions to think about
            </p>
            <ul className="list-disc space-y-1 pl-5 text-sm text-foreground/85">
              {starterQuestions.map((q, i) => (
                <li key={i}>{q}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {sectionKind === "body" && (
      <div className="mb-3 rounded-lg border border-dashed border-primary/30 bg-primary/5 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Quote className="h-3.5 w-3.5 text-primary" aria-hidden />
          <span className="text-xs font-medium text-foreground/80">
            Pull quotes from the source for this {sectionKind === "body" ? "paragraph" : sectionKind}:
          </span>
          <Select value={String(quoteCount)} onValueChange={(v) => setQuoteCount(Number(v))} disabled={disabled || findingQuotes}>
            <SelectTrigger className="h-8 w-[110px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5].map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n} quote{n === 1 ? "" : "s"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onFindQuotes}
            disabled={disabled || findingQuotes}
            className="gap-1.5"
          >
            {findingQuotes ? (
              <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Finding…</>
            ) : (
              <><Quote className="h-3.5 w-3.5" /> Find quotes</>
            )}
          </Button>
        </div>
        {!sourceText && (
          <p className="mt-2 text-[11px] text-muted-foreground">
            Tip: paste your source text in the setup step so I can pull real quotes from it.
          </p>
        )}
        {quotes && quotes.length > 0 && (
          <ul className="mt-3 space-y-2">
            {quotes.map((q, i) => (
              <li key={i} className="rounded-md border border-border bg-background p-3">
                <blockquote className="text-sm italic leading-relaxed text-foreground">
                  "{q.quote}"
                </blockquote>
                <p className="mt-1 text-xs text-muted-foreground">— {q.citation}</p>
                {q.why_it_works && (
                  <p className="mt-1.5 text-xs text-foreground/85">💡 {q.why_it_works}</p>
                )}
                <div className="mt-2">
                  <Button type="button" size="sm" variant="ghost" className="h-7 gap-1 px-2 text-xs" onClick={() => copyQuote(q)}>
                    <Check className="h-3 w-3" /> Copy quote
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      )}



      <div className="relative">
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Write your ${label.toLowerCase()} here…`}
          className="min-h-[120px] pr-12"
          disabled={disabled}
          spellCheck
          autoCorrect="on"
          autoCapitalize="sentences"
        />
        {(autoCorrecting || justCorrected) && (
          <div className="pointer-events-none absolute bottom-2 left-2 flex items-center gap-1 rounded-md bg-background/80 px-1.5 py-0.5 text-[10px] text-muted-foreground backdrop-blur">
            {autoCorrecting ? (
              <><Loader2 className="h-3 w-3 animate-spin" /> Auto-correcting…</>
            ) : (
              <><Check className="h-3 w-3 text-primary" /> Auto-corrected</>
            )}
          </div>
        )}
        <div className="absolute right-2 top-2">
          <MicButton
            label={label.toLowerCase()}
            value={value}
            onChange={onChange}
          />
        </div>
      </div>

      <div className="mt-3 flex items-center justify-end">
        <Button
          type="button"
          size="sm"
          onClick={onCheck}
          disabled={disabled || checking || !value.trim()}
          className="gap-1.5"
        >
          {checking ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking…
            </>
          ) : (
            <>
              <Check className="h-3.5 w-3.5" /> Check my {sectionKind === "body" ? "paragraph" : sectionKind}
            </>
          )}
        </Button>
      </div>

      {feedback && (
        <div className="mt-3 space-y-3 rounded-lg border border-border bg-background p-4">
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                ratingStyles[feedback.rating]?.chip ?? ratingStyles.almost.chip
              }`}
            >
              {ratingStyles[feedback.rating]?.label ?? "Nice work"}
            </span>
          </div>
          <p className="text-sm leading-relaxed text-foreground">
            <span className="font-medium">✨ {feedback.praise}</span>
          </p>
          {feedback.suggestions?.length > 0 && (
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Gentle suggestions
              </p>
              <ul className="mt-1.5 space-y-1 text-sm text-foreground/80">
                {feedback.suggestions.map((s, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-primary" aria-hidden>—</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {feedback.encouragement && (
            <p className="rounded-md bg-accent/40 p-2.5 text-sm italic text-foreground">
              💛 {feedback.encouragement}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- CER body-paragraph builder (Claim → Evidence → Reasoning) ----------

type CERQuote = { quote: string; citation: string; why_it_works: string };

function assembleCER(claim: string, evs: CERQuote[], reasoning: string) {
  const parts: string[] = [];
  if (claim.trim()) parts.push(claim.trim());
  for (const ev of evs) {
    const cite = ev.citation ? ` ${ev.citation}` : "";
    parts.push(`"${ev.quote.trim()}"${cite}.`);
  }
  if (reasoning.trim()) parts.push(reasoning.trim());
  return parts.join(" ");
}

function CERBody({
  index,
  topic,
  target,
  points,
  value,
  onChange,
  disabled,
  thesis,
  essayType,
  teacherPrompt,
  sourceTitle,
  sourceText,
}: {
  index: number;
  topic?: string;
  target: number;
  points: string[];
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  thesis: string;
  essayType: string;
  teacherPrompt: string;
  sourceTitle: string;
  sourceText: string;
}) {
  const label = `Body Paragraph ${index + 1}${topic ? ` — ${topic}` : ""}`;
  const quotesFn = useServerFn(findQuotes);
  const checkFn = useServerFn(checkParagraph);
  const checkClaimFn = useServerFn(checkClaim);

  // CER pieces
  const [claim, setClaim] = useState("");
  const [reasoning, setReasoning] = useState("");
  const [quoteCount, setQuoteCount] = useState(3);
  const [findingQuotes, setFindingQuotes] = useState(false);
  const [quotes, setQuotes] = useState<CERQuote[] | null>(null);
  const [selectedQuotes, setSelectedQuotes] = useState<number[]>([]);

  // Manual evidence entry (student types their own quote)
  const [evidenceMode, setEvidenceMode] = useState<"suggested" | "manual">("suggested");
  const [manualQuote, setManualQuote] = useState("");
  const [manualCitation, setManualCitation] = useState("");

  // Per-step claim feedback
  const [checkingClaim, setCheckingClaim] = useState(false);
  const [claimAttempts, setClaimAttempts] = useState(0);
  const [claimFeedback, setClaimFeedback] = useState<{
    rating: "great" | "almost" | "needs_work";
    praise: string;
    suggestions: string[];
    encouragement: string;
  } | null>(null);

  const [checking, setChecking] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [feedback, setFeedback] = useState<{
    rating: "great" | "almost" | "needs_work";
    praise: string;
    suggestions: string[];
    encouragement: string;
  } | null>(null);

  // Treat existing legacy draft as already-assembled — show as starting reasoning only if nothing else
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (!hydrated && value && !claim && !reasoning) {
      // Best-effort: keep as reasoning so resumed users can still edit; they can clear and restart.
      setReasoning(value);
      setHydrated(true);
    } else if (!hydrated) {
      setHydrated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const suggestedEvidence: CERQuote[] = quotes
    ? selectedQuotes
        .filter((i) => i >= 0 && i < quotes.length)
        .map((i) => quotes[i])
    : [];
  const manualEvidence: CERQuote[] =
    evidenceMode === "manual" && manualQuote.trim()
      ? [{ quote: manualQuote.trim(), citation: manualCitation.trim(), why_it_works: "" }]
      : [];
  const evidenceList = evidenceMode === "manual" ? manualEvidence : suggestedEvidence;
  const evidenceDone = evidenceList.length > 0;
  const assembled = assembleCER(claim, evidenceList, reasoning);
  const words = countWords(assembled);
  const onTrack = target > 0 ? Math.abs(words - target) <= Math.max(20, target * 0.15) : true;

  // Keep parent drafts[] in sync with assembled CER paragraph
  useEffect(() => {
    if (!hydrated) return;
    if (assembled !== value) onChange(assembled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assembled, hydrated]);

  async function onFindEvidence() {
    if (!claim.trim()) {
      toast.error("Write your claim first — I'll pull quotes that back it up.");
      return;
    }
    if ((!sourceText || sourceText.trim().length < 20) && (!sourceTitle || sourceTitle.trim().length < 2)) {
      toast.error("Add a source title or paste your source text up top first.");
      return;
    }
    setFindingQuotes(true);
    try {
      const res = await quotesFn({
        data: {
          section_kind: "body",
          section_label: label,
          section_topic: topic || claim,
          count: quoteCount,
          thesis: thesis || undefined,
          draft: claim,
          essay_type: essayType || undefined,
          prompt: teacherPrompt || undefined,
          source_title: sourceTitle || undefined,
          source_text: sourceText,
        },
      });
      setQuotes(res.quotes);
      setSelectedQuotes([]);
      if (res.quotes.length === 0) {
        toast.message("Couldn't find clean evidence. Try sharpening your claim or pick a different count.");
      }
    } catch (err) {
      toast.error((err as Error).message || "Couldn't find evidence. Try again.");
    } finally {
      setFindingQuotes(false);
    }
  }

  async function onCheckClaim() {
    if (!claim.trim()) return toast.error("Write your claim first.");
    setCheckingClaim(true);
    setClaimFeedback(null);
    try {
      const nextAttempt = claimAttempts + 1;
      const res = await checkClaimFn({
        data: {
          claim: claim.trim(),
          section_label: label,
          section_topic: topic || undefined,
          thesis: thesis || undefined,
          essay_type: essayType || undefined,
          prompt: teacherPrompt || undefined,
          attempt: nextAttempt,
        },
      });
      setClaimAttempts(nextAttempt);
      setClaimFeedback(res.feedback);
    } catch (err) {
      toast.error((err as Error).message || "Couldn't check your claim. Try again.");
    } finally {
      setCheckingClaim(false);
    }
  }

  async function onCheckParagraph() {
    if (!claim.trim()) return toast.error("Write your claim first.");
    if (!evidenceDone) return toast.error("Pick at least one piece of evidence first.");
    if (!reasoning.trim()) return toast.error("Add your reasoning — why does this evidence prove your claim?");
    setChecking(true);
    setFeedback(null);
    try {
      const nextAttempt = attempts + 1;
      const res = await checkFn({
        data: {
          section_kind: "body",
          section_label: label,
          section_topic: topic || undefined,
          draft: assembled,
          thesis: thesis || undefined,
          essay_type: essayType || undefined,
          prompt: teacherPrompt || undefined,
          source_title: sourceTitle || undefined,
          source_text: sourceText || undefined,
          attempt: nextAttempt,
        },
      });
      setAttempts(nextAttempt);
      setFeedback(res.feedback);
    } catch (err) {
      toast.error((err as Error).message || "Couldn't check your paragraph. Try again.");
    } finally {
      setChecking(false);
    }
  }

  const ratingStyles: Record<string, { label: string; chip: string }> = {
    great: { label: "Great paragraph!", chip: "bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/30" },
    almost: { label: "Almost there", chip: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30" },
    needs_work: { label: "Good start — let's strengthen it", chip: "bg-primary/15 text-primary border-primary/30" },
  };

  const stepBadge = (n: number, active: boolean, done: boolean) => (
    <span
      className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
        done
          ? "bg-primary text-primary-foreground"
          : active
            ? "bg-primary/15 text-primary border border-primary/40"
            : "bg-muted text-muted-foreground"
      }`}
    >
      {done ? "✓" : n}
    </span>
  );

  const claimDone = claim.trim().length > 0;
  const reasoningDone = reasoning.trim().length > 0;

  return (
    <div className="rounded-xl border border-border bg-background/40 p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="font-display text-base font-medium">{label}</h3>
        <span className={`text-xs ${onTrack ? "text-muted-foreground" : "text-primary"}`}>
          {words}{target ? ` / ~${target}` : ""} words
        </span>
      </div>

      {points.length > 0 && (
        <div className="mb-3 rounded-md bg-muted/40 p-3">
          <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Think about these questions
          </p>
          <ul className="mt-1 space-y-1 text-sm text-foreground/80">
            {points.map((p, i) => (
              <li key={i} className="flex gap-2"><span className="text-primary" aria-hidden>—</span><span>{p}</span></li>
            ))}
          </ul>
        </div>
      )}

      {/* STEP 1 — CLAIM */}
      <div className="mb-4 rounded-lg border border-border bg-background p-3">
        <div className="mb-2 flex items-center gap-2">
          {stepBadge(1, !claimDone, claimDone)}
          <span className="text-xs font-semibold uppercase tracking-wider text-foreground">Claim</span>
          <span className="text-xs text-muted-foreground">— the point this paragraph makes</span>
        </div>
        <div className="relative">
          <Textarea
            value={claim}
            onChange={(e) => setClaim(e.target.value)}
            placeholder="Write your topic sentence — one clear claim that supports your thesis."
            className="min-h-[70px] pr-12"
            disabled={disabled}
          />
          <div className="absolute right-2 top-2">
            <MicButton label="claim" value={claim} onChange={setClaim} />
          </div>
        </div>
        <div className="mt-2 flex items-center justify-end">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onCheckClaim}
            disabled={disabled || checkingClaim || !claimDone}
            className="gap-1.5"
          >
            {checkingClaim ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking…</> : <><Check className="h-3.5 w-3.5" /> Check my claim</>}
          </Button>
        </div>
        {claimFeedback && (
          <div className="mt-3 space-y-2 rounded-md border border-border bg-muted/30 p-3">
            <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${ratingStyles[claimFeedback.rating]?.chip ?? ratingStyles.almost.chip}`}>
              {claimFeedback.rating === "great" ? "Great claim!" : claimFeedback.rating === "almost" ? "Almost there" : "Good start — let's sharpen it"}
            </span>
            <p className="text-sm leading-relaxed text-foreground">✨ {claimFeedback.praise}</p>
            {claimFeedback.suggestions?.length > 0 && (
              <ul className="space-y-1 text-sm text-foreground/80">
                {claimFeedback.suggestions.map((s, i) => (
                  <li key={i} className="flex gap-2"><span className="text-primary" aria-hidden>—</span><span>{s}</span></li>
                ))}
              </ul>
            )}
            {claimFeedback.encouragement && (
              <p className="text-xs italic text-muted-foreground">💛 {claimFeedback.encouragement}</p>
            )}
          </div>
        )}
      </div>

      {/* STEP 2 — EVIDENCE */}
      <div className={`mb-4 rounded-lg border p-3 ${claimDone || evidenceMode === "manual" ? "border-border bg-background" : "border-dashed border-border bg-muted/30"}`}>
        <div className="mb-2 flex items-center gap-2">
          {stepBadge(2, claimDone && !evidenceDone, evidenceDone)}
          <span className="text-xs font-semibold uppercase tracking-wider text-foreground">Evidence</span>
          <span className="text-xs text-muted-foreground">— a real quote from the source</span>
        </div>

        {/* Mode toggle */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">How do you want to add it?</span>
          <div className="inline-flex rounded-md border border-border bg-muted/40 p-0.5 text-xs">
            <button
              type="button"
              onClick={() => setEvidenceMode("suggested")}
              disabled={disabled}
              className={`rounded px-2.5 py-1 transition ${evidenceMode === "suggested" ? "bg-background shadow-sm text-foreground font-medium" : "text-muted-foreground hover:text-foreground"}`}
            >
              Suggest from source
            </button>
            <button
              type="button"
              onClick={() => setEvidenceMode("manual")}
              disabled={disabled}
              className={`rounded px-2.5 py-1 transition ${evidenceMode === "manual" ? "bg-background shadow-sm text-foreground font-medium" : "text-muted-foreground hover:text-foreground"}`}
            >
              Type my own quote
            </button>
          </div>
        </div>


        {evidenceMode === "suggested" ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Quote className="h-3.5 w-3.5 text-primary" aria-hidden />
              <span className="text-xs">Suggest</span>
              <Select value={String(quoteCount)} onValueChange={(v) => setQuoteCount(Number(v))} disabled={disabled || findingQuotes || !claimDone}>
                <SelectTrigger className="h-8 w-[110px] text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <SelectItem key={n} value={String(n)}>{n} quote{n === 1 ? "" : "s"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={onFindEvidence}
                disabled={disabled || findingQuotes || !claimDone}
                className="gap-1.5"
              >
                {findingQuotes ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Finding…</> : <><Quote className="h-3.5 w-3.5" /> Suggest evidence</>}
              </Button>
            </div>
            {!claimDone && (
              <p className="mt-2 text-[11px] text-muted-foreground">Write your claim above first — I'll find quotes that back it up.</p>
            )}
            {quotes && quotes.length > 0 && (
              <>
                <p className="mt-3 text-[11px] text-muted-foreground">Tap to add a quote to your paragraph. You can pick more than one.</p>
                <ul className="mt-1.5 space-y-2">
                  {quotes.map((q, i) => {
                    const isSelected = selectedQuotes.includes(i);
                    return (
                      <li key={i}>
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedQuotes((prev) =>
                              prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i],
                            )
                          }
                          className={`w-full rounded-md border p-3 text-left transition ${
                            isSelected ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border bg-background hover:border-primary/40"
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <span className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border ${isSelected ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40"}`}>
                              {isSelected && <Check className="h-3 w-3" />}
                            </span>
                            <div className="flex-1">
                            <blockquote className="text-sm italic leading-relaxed text-foreground">"{q.quote}"</blockquote>
                            <p className="mt-1 text-xs text-muted-foreground">— {q.citation}</p>
                            {q.why_it_works && <p className="mt-1.5 text-xs text-foreground/85">💡 {q.why_it_works}</p>}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
                </ul>
              </>
            )}
          </>
        ) : (
          <div className="space-y-2">
            <div className="relative">
              <Textarea
                value={manualQuote}
                onChange={(e) => setManualQuote(e.target.value)}
                placeholder='Paste or type your quote here (without the quote marks — we add them).'
                className="min-h-[80px] pr-12"
                disabled={disabled}
              />
              <div className="absolute right-2 top-2">
                <MicButton label="quote" value={manualQuote} onChange={setManualQuote} />
              </div>
            </div>
            <Input
              value={manualCitation}
              onChange={(e) => setManualCitation(e.target.value)}
              placeholder='Citation (e.g. "Fitzgerald 23" or "Smith, 2019, p. 12")'
              disabled={disabled}
            />
            <p className="text-[11px] text-muted-foreground">
              Use this when your quote is from a source the tutor can't see, or you already have one picked out.
            </p>
          </div>
        )}
      </div>



      {/* STEP 3 — REASONING */}
      <div className={`mb-3 rounded-lg border p-3 ${evidenceDone ? "border-border bg-background" : "border-dashed border-border bg-muted/30 opacity-70"}`}>
        <div className="mb-2 flex items-center gap-2">
          {stepBadge(3, evidenceDone && !reasoningDone, reasoningDone)}
          <span className="text-xs font-semibold uppercase tracking-wider text-foreground">Reasoning</span>
          <span className="text-xs text-muted-foreground">— why this evidence proves your claim</span>
        </div>
        <div className="relative">
          <Textarea
            value={reasoning}
            onChange={(e) => setReasoning(e.target.value)}
            placeholder="In your own words: how does this quote prove the claim you made? Why does it matter for your thesis?"
            className="min-h-[110px] pr-12"
            disabled={disabled || !evidenceDone}
          />
          <div className="absolute right-2 top-2">
            <MicButton label="reasoning" value={reasoning} onChange={setReasoning} />
          </div>
        </div>
        {!evidenceDone && (
          <p className="mt-2 text-[11px] text-muted-foreground">Pick a piece of evidence above first.</p>
        )}
      </div>

      {/* Assembled preview */}
      {(claim || evidenceDone || reasoning) && (
        <div className="mb-3 rounded-md border border-dashed border-primary/30 bg-primary/5 p-3">
          <p className="text-[11px] font-medium uppercase tracking-wider text-primary">Your paragraph so far</p>
          <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground">{assembled || "—"}</p>
        </div>
      )}

      <div className="flex items-center justify-end">
        <Button
          type="button"
          size="sm"
          onClick={onCheckParagraph}
          disabled={disabled || checking || !claimDone || !evidenceDone || !reasoningDone}
          className="gap-1.5"
        >
          {checking ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking…</> : <><Check className="h-3.5 w-3.5" /> Check my paragraph</>}
        </Button>
      </div>

      {feedback && (
        <div className="mt-3 space-y-3 rounded-lg border border-border bg-background p-4">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${ratingStyles[feedback.rating]?.chip ?? ratingStyles.almost.chip}`}>
              {ratingStyles[feedback.rating]?.label ?? "Nice work"}
            </span>
          </div>
          <p className="text-sm leading-relaxed text-foreground"><span className="font-medium">✨ {feedback.praise}</span></p>
          {feedback.suggestions?.length > 0 && (
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Gentle suggestions</p>
              <ul className="mt-1.5 space-y-1 text-sm text-foreground/80">
                {feedback.suggestions.map((s, i) => (
                  <li key={i} className="flex gap-2"><span className="text-primary" aria-hidden>—</span><span>{s}</span></li>
                ))}
              </ul>
            </div>
          )}
          {feedback.encouragement && (
            <p className="rounded-md bg-accent/40 p-2.5 text-sm italic text-foreground">💛 {feedback.encouragement}</p>
          )}
        </div>
      )}
    </div>
  );
}


function OutlineView({
  outline,
  drafts,
  setDraft,
  disabled,
  essayType,
  teacherPrompt,
  sourceTitle,
  sourceText,
  thesis,
  setThesis,
  onSave,
  onClearForm,
  saving,
  workId,
  lastSavedAt,
}: {
  outline: OutlineData;
  drafts: string[];
  setDraft: (i: number, v: string) => void;
  disabled?: boolean;
  essayType: string;
  teacherPrompt: string;
  sourceTitle: string;
  sourceText: string;
  thesis: string;
  setThesis: (v: string) => void;
  onSave: () => void;
  onClearForm: () => void;
  saving: boolean;
  workId: string | null;
  lastSavedAt: Date | null;
}) {
  const checkThesisFn = useServerFn(checkThesis);
  const suggestQuestionsFn = useServerFn(suggestThesisQuestions);
  const [checkingThesis, setCheckingThesis] = useState(false);
  const [thesisAttempts, setThesisAttempts] = useState(0);
  const [feedback, setFeedback] = useState<{
    rating: "great" | "almost" | "needs_work";
    praise: string;
    suggestions: string[];
    encouragement: string;
    example_thesis?: string;
  } | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [starterQuestions, setStarterQuestions] = useState<string[] | null>(null);

  async function onSuggest() {
    setSuggesting(true);
    try {
      const res = await suggestQuestionsFn({
        data: {
          essay_type: essayType,
          prompt: teacherPrompt || undefined,
          source_title: sourceTitle || undefined,
          source_text: sourceText || undefined,
        },
      });
      setStarterQuestions(res.questions);
    } catch (err) {
      toast.error((err as Error).message || "Couldn't get suggestions. Try again.");
    } finally {
      setSuggesting(false);
    }
  }

  async function onCheckThesis() {
    const t = thesis.trim();
    if (!t) {
      toast.error("Type your thesis first — even a rough first try is great!");
      return;
    }
    setCheckingThesis(true);
    setFeedback(null);
    try {
      const nextAttempt = thesisAttempts + 1;
      const res = await checkThesisFn({
        data: {
          thesis: t,
          essay_type: essayType,
          prompt: teacherPrompt || undefined,
          source_title: sourceTitle || undefined,
          source_text: sourceText || undefined,
          attempt: nextAttempt,
          previous_example_thesis: feedback?.example_thesis || undefined,
        },
      });
      setThesisAttempts(nextAttempt);
      setFeedback(res.feedback);
    } catch (err) {
      toast.error((err as Error).message || "Couldn't check your thesis. Try again.");
    } finally {
      setCheckingThesis(false);
    }
  }

  const total = drafts.reduce((sum, d) => sum + countWords(d), 0);
  const target =
    outline.introduction.target_words +
    outline.body_paragraphs.reduce((s, b) => s + b.target_words, 0) +
    outline.conclusion.target_words;

  const ratingStyles: Record<string, { label: string; chip: string }> = {
    great: { label: "Great thesis!", chip: "bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/30" },
    almost: { label: "Almost there", chip: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30" },
    needs_work: { label: "Good start — let's strengthen it", chip: "bg-primary/15 text-primary border-primary/30" },
  };

  return (
    <section className="mt-6 space-y-4 rounded-2xl border border-border bg-card p-5 shadow-soft sm:p-6">
      {/* Save bar at top of outline */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-background/50 p-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Save className="h-3.5 w-3.5" />
          <span>
            {saving
              ? "Saving…"
              : lastSavedAt || workId
              ? "Autosaving your work as you go — find it in My saved work."
              : "Your work autosaves once you start. You can also save manually anytime."}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {lastSavedAt && (
            <span className="hidden text-xs text-muted-foreground sm:inline">
              Saved {lastSavedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
            </span>
          )}
          <Button asChild variant="ghost" size="sm" className="h-7 rounded-full text-xs">
            <Link to="/history">My saved work</Link>
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onClearForm}
            disabled={saving}
            className="h-7 rounded-full text-xs"
          >
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
            Start over
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onSave}
            disabled={saving || disabled}
            className="h-7 rounded-full text-xs"
          >
            {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}
            {workId ? "Save" : "Save my work"}
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 border-l-2 border-gold pl-3">
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary text-xs font-bold text-primary-foreground">2</span>
          <ListChecks className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-medium uppercase tracking-[0.18em] text-primary">Your outline & drafting</h2>
        </div>
        <span className="text-xs text-muted-foreground">
          Total: {total}{target ? ` / ~${target}` : ""} words
        </span>
      </div>

      {outline.thesis_hint && (
        <p className="rounded-lg border border-gold/40 bg-accent/30 p-3 text-sm">
          <span className="font-medium">Thesis direction: </span>
          {outline.thesis_hint}
        </p>
      )}

      {/* Thesis box */}
      <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
        <div className="mb-2 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <h3 className="font-display text-base font-medium">Your thesis statement</h3>
        </div>
        <p className="mb-3 text-xs text-muted-foreground">
          Write your thesis in your own words — then check it for friendly feedback. There's no wrong first try!
        </p>
        <div className="mb-3">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onSuggest}
            disabled={disabled || suggesting}
            className="gap-1.5"
          >
            {suggesting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <HelpCircle className="h-3.5 w-3.5" />
            )}
            {starterQuestions ? "Get more questions" : "Help me start my thesis"}
          </Button>
          {starterQuestions && starterQuestions.length > 0 && (
            <div className="mt-3 rounded-lg border border-gold/40 bg-accent/30 p-3">
              <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-primary">
                Questions to think about
              </p>
              <ul className="list-disc space-y-1 pl-5 text-sm text-foreground/85">
                {starterQuestions.map((q, i) => (
                  <li key={i}>{q}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="relative">
          <Textarea
            value={thesis}
            onChange={(e) => setThesis(e.target.value)}
            placeholder="Type your thesis here…"
            className="min-h-[90px] pr-12"
            disabled={disabled}
          />
          <div className="absolute right-2 top-2">
            <MicButton
              label="your thesis"
              value={thesis}
              onChange={setThesis}
            />
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">{countWords(thesis)} words</span>
          <Button
            type="button"
            size="sm"
            onClick={onCheckThesis}
            disabled={disabled || checkingThesis || !thesis.trim()}
            className="gap-1.5"
          >
            {checkingThesis ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking…
              </>
            ) : (
              <>
                <Check className="h-3.5 w-3.5" /> Check my thesis
              </>
            )}
          </Button>
        </div>

        {feedback && (
          <div className="mt-4 space-y-3 rounded-lg border border-border bg-background p-4">
            <div className="flex items-center gap-2">
              <span
                className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                  ratingStyles[feedback.rating]?.chip ?? ratingStyles.almost.chip
                }`}
              >
                {ratingStyles[feedback.rating]?.label ?? "Nice work"}
              </span>
            </div>
            <p className="text-sm leading-relaxed text-foreground">
              <span className="font-medium">✨ {feedback.praise}</span>
            </p>
            {feedback.suggestions?.length > 0 && (
              <div>
                <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  Gentle suggestions
                </p>
                <ul className="mt-1.5 space-y-1 text-sm text-foreground/80">
                  {feedback.suggestions.map((s, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-primary" aria-hidden>—</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {feedback.encouragement && (
              <p className="rounded-md bg-accent/40 p-2.5 text-sm italic text-foreground">
                💛 {feedback.encouragement}
              </p>
            )}
            {feedback.example_thesis && feedback.example_thesis.trim() && (
              <div className="rounded-md border border-primary/30 bg-primary/5 p-3">
                <p className="mb-1 text-[11px] font-medium uppercase tracking-wider text-primary">
                  Example thesis you could model yours after
                </p>
                <p className="text-sm italic text-foreground">"{feedback.example_thesis}"</p>
                <p className="mt-2 text-xs text-muted-foreground">
                  This is just an example — try rewriting it in your own words so it sounds like you.
                </p>
              </div>
            )}
          </div>
        )}
      </div>


      <div className="space-y-3">
        <SectionDraft
          label="Introduction"
          target={outline.introduction.target_words}
          points={outline.introduction.points}
          value={drafts[0] ?? ""}
          onChange={(v) => setDraft(0, v)}
          disabled={disabled}
          sectionKind="introduction"
          thesis={thesis}
          essayType={essayType}
          teacherPrompt={teacherPrompt}
          sourceTitle={sourceTitle}
          sourceText={sourceText}
        />
        {outline.body_paragraphs.map((b, i) => (
          <CERBody
            key={i}
            index={i}
            topic={b.topic}
            target={b.target_words}
            points={b.points}
            value={drafts[i + 1] ?? ""}
            onChange={(v) => setDraft(i + 1, v)}
            disabled={disabled}
            thesis={thesis}
            essayType={essayType}
            teacherPrompt={teacherPrompt}
            sourceTitle={sourceTitle}
            sourceText={sourceText}
          />
        ))}
        <SectionDraft
          label="Conclusion"
          target={outline.conclusion.target_words}
          points={outline.conclusion.points}
          value={drafts[outline.body_paragraphs.length + 1] ?? ""}
          onChange={(v) => setDraft(outline.body_paragraphs.length + 1, v)}
          disabled={disabled}
          sectionKind="conclusion"
          thesis={thesis}
          essayType={essayType}
          teacherPrompt={teacherPrompt}
          sourceTitle={sourceTitle}
          sourceText={sourceText}
        />
      </div>

      <WrapUp
        outline={outline}
        drafts={drafts}
        thesis={thesis}
        essayType={essayType}
        teacherPrompt={teacherPrompt}
        sourceTitle={sourceTitle}
        disabled={disabled}
      />
    </section>
  );
}

function WrapUp({
  outline,
  drafts,
  thesis,
  essayType,
  teacherPrompt,
  sourceTitle,
  disabled,
}: {
  outline: OutlineData;
  drafts: string[];
  thesis: string;
  essayType: string;
  teacherPrompt: string;
  sourceTitle: string;
  disabled?: boolean;
}) {
  const gradeFn = useServerFn(gradeBrainwork);
  const [grading, setGrading] = useState(false);
  const [report, setReport] = useState<{
    grade: string;
    summary: string;
    strengths: string[];
    growth_areas: string[];
    encouragement: string;
  } | null>(null);

  // MLA header
  const [studentName, setStudentName] = useState("");
  const [instructor, setInstructor] = useState("");
  const [course, setCourse] = useState("");
  const [paperDate, setPaperDate] = useState("");
  const [paperTitle, setPaperTitle] = useState("");
  const [exporting, setExporting] = useState(false);

  const introDraft = drafts[0] ?? "";
  const bodyDrafts = outline.body_paragraphs.map((b, i) => ({
    topic: b.topic,
    text: drafts[i + 1] ?? "",
  }));
  const conclusionDraft = drafts[outline.body_paragraphs.length + 1] ?? "";
  const conclusionDone = countWords(conclusionDraft) >= 20;

  async function onGrade() {
    setGrading(true);
    setReport(null);
    try {
      const res = await gradeFn({
        data: {
          essay_type: essayType,
          prompt: teacherPrompt || undefined,
          thesis: thesis || undefined,
          introduction: introDraft || undefined,
          body_paragraphs: bodyDrafts.filter((b) => b.text.trim()).map((b) => ({
            topic: b.topic,
            text: b.text,
          })),
          conclusion: conclusionDraft || undefined,
          source_title: sourceTitle || undefined,
        },
      });
      setReport(res.report);
    } catch (err) {
      toast.error((err as Error).message || "Couldn't grade your work. Try again.");
    } finally {
      setGrading(false);
    }
  }

  async function onExportMla() {
    if (!studentName.trim()) {
      toast.error("Add your name so we can put it on the MLA heading.");
      return;
    }
    setExporting(true);
    try {
      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF({ unit: "in", format: "letter" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 1;
      const usableW = pageW - margin * 2;
      const lineHeight = 0.32; // double-spaced 12pt

      doc.setFont("times", "normal");
      doc.setFontSize(12);

      let y = margin;
      const lastName = studentName.trim().split(/\s+/).pop() ?? "";

      const ensure = (n = lineHeight) => {
        if (y + n > pageH - margin) {
          doc.addPage();
          y = margin;
        }
      };
      const write = (text: string, opts?: { align?: "left" | "center"; indent?: number }) => {
        const align = opts?.align ?? "left";
        const indent = opts?.indent ?? 0;
        const lines = doc.splitTextToSize(text, usableW - indent);
        for (const ln of lines) {
          ensure();
          if (align === "center") doc.text(ln, pageW / 2, y, { align: "center" });
          else doc.text(ln, margin + indent, y);
          y += lineHeight;
        }
      };
      const writeParagraph = (text: string) => {
        // MLA: first-line indent 0.5", double-spaced, no extra space between paragraphs
        const t = text.trim();
        if (!t) return;
        // Split user's paragraph breaks
        const paras = t.split(/\n\s*\n+/);
        for (const para of paras) {
          const clean = para.replace(/\s+/g, " ").trim();
          if (!clean) continue;
          const firstLineWidth = usableW - 0.5;
          const wrapped = doc.splitTextToSize(clean, firstLineWidth);
          // Render first line with indent, then rest at margin (MLA uses uniform first-line indent only)
          ensure();
          doc.text(wrapped[0] ?? "", margin + 0.5, y);
          y += lineHeight;
          for (let i = 1; i < wrapped.length; i++) {
            ensure();
            doc.text(wrapped[i], margin, y);
            y += lineHeight;
          }
        }
      };

      // MLA heading block (top-left, double-spaced)
      write(studentName.trim());
      write(instructor.trim() || "Instructor");
      write(course.trim() || "Course");
      write(paperDate.trim() || new Date().toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" }));

      // Title centered
      const title = paperTitle.trim() || (teacherPrompt.trim().slice(0, 80) || "Untitled Essay");
      write(title, { align: "center" });

      // Body: intro, body paragraphs, conclusion
      if (introDraft.trim()) writeParagraph(introDraft);
      for (const b of bodyDrafts) {
        if (b.text.trim()) writeParagraph(b.text);
      }
      if (conclusionDraft.trim()) writeParagraph(conclusionDraft);

      // Page numbers top-right: "LastName #"
      const totalPages = doc.getNumberOfPages();
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        doc.setFont("times", "normal");
        doc.setFontSize(12);
        doc.text(`${lastName} ${p}`, pageW - margin, margin - 0.4, { align: "right" });
      }

      const safe = (paperTitle.trim() || studentName.trim() || "essay")
        .replace(/[^a-z0-9]+/gi, "_")
        .slice(0, 60);
      doc.save(`${safe}_MLA.pdf`);
      toast.success("MLA essay downloaded — nice work!");
    } catch (err) {
      toast.error((err as Error).message || "Couldn't build the PDF.");
    } finally {
      setExporting(false);
    }
  }

  return (
    <div id="step-finish" className="mt-6 space-y-4 scroll-mt-32 rounded-2xl border border-gold/40 bg-accent/20 p-5">
      <div className="flex items-center gap-3 border-l-2 border-gold pl-3">
        <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary text-xs font-bold text-primary-foreground">4</span>
        <Award className="h-4 w-4 text-primary" />
        <h2 className="text-sm font-medium uppercase tracking-[0.18em] text-primary">Wrap up & celebrate</h2>
      </div>
      <p className="text-sm text-muted-foreground">
        {conclusionDone
          ? "You did the thinking — every word is yours. Get a grade on your brainwork, then export your finished essay in MLA format."
          : "Once you finish your conclusion, you'll be able to grade your work and download it in MLA format."}
      </p>

      {/* Grade */}
      <div className="rounded-xl border border-border bg-background p-4">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h3 className="font-display text-base font-medium">Grade my brainwork</h3>
            <p className="text-xs text-muted-foreground">An honest, kind grade from your AI tutor.</p>
          </div>
          <Button type="button" size="sm" onClick={onGrade} disabled={disabled || grading} className="gap-1.5">
            {grading ? (
              <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Grading…</>
            ) : (
              <><Award className="h-3.5 w-3.5" /> {report ? "Re-grade" : "Grade my work"}</>
            )}
          </Button>
        </div>

        {report && (
          <div className="mt-4 space-y-3">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
                Grade: {report.grade}
              </span>
            </div>
            <p className="text-sm leading-relaxed">✨ {report.summary}</p>
            {report.strengths?.length > 0 && (
              <div>
                <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Strengths</p>
                <ul className="mt-1.5 space-y-1 text-sm text-foreground/85">
                  {report.strengths.map((s, i) => (
                    <li key={i} className="flex gap-2"><span className="text-primary">—</span><span>{s}</span></li>
                  ))}
                </ul>
              </div>
            )}
            {report.growth_areas?.length > 0 && (
              <div>
                <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Growth areas</p>
                <ul className="mt-1.5 space-y-1 text-sm text-foreground/85">
                  {report.growth_areas.map((s, i) => (
                    <li key={i} className="flex gap-2"><span className="text-primary">—</span><span>{s}</span></li>
                  ))}
                </ul>
              </div>
            )}
            {report.encouragement && (
              <p className="rounded-md bg-accent/40 p-2.5 text-sm italic">💛 {report.encouragement}</p>
            )}
          </div>
        )}
      </div>

      {/* MLA Export */}
      <div className="rounded-xl border border-border bg-background p-4">
        <div className="mb-3">
          <h3 className="font-display text-base font-medium">Download in MLA format</h3>
          <p className="text-xs text-muted-foreground">
            Exports the words YOU wrote — thesis, intro, body, and conclusion — into a properly formatted MLA PDF you can turn in.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Your name</Label>
            <Input value={studentName} onChange={(e) => setStudentName(e.target.value)} placeholder="Jane Doe" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Instructor</Label>
            <Input value={instructor} onChange={(e) => setInstructor(e.target.value)} placeholder="Ms. Smith" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Course</Label>
            <Input value={course} onChange={(e) => setCourse(e.target.value)} placeholder="English 10" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Date</Label>
            <Input value={paperDate} onChange={(e) => setPaperDate(e.target.value)} placeholder="31 May 2026" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Essay title</Label>
            <Input value={paperTitle} onChange={(e) => setPaperTitle(e.target.value)} placeholder="Give your essay a title" />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button type="button" onClick={onExportMla} disabled={disabled || exporting || !conclusionDone}>
            {exporting ? (
              <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Building PDF…</>
            ) : (
              <><FileDown className="mr-1.5 h-4 w-4" /> Download MLA PDF</>
            )}
          </Button>
        </div>
        {!conclusionDone && (
          <p className="mt-2 text-right text-[11px] text-muted-foreground">
            Finish your conclusion to unlock the MLA download.
          </p>
        )}
      </div>
    </div>
  );
}
